import axios from 'axios'
import { createRouter, createWebHistory } from 'vue-router'
import i18n from '../i18n'
import Admin from '@/modules/admin/Admin.vue'
import Auth from '@/services/auth'
import Browse from '@/modules/browse/Browse.vue'
import CatalogBase from '@/modules/catalogBase/CatalogBase.vue'
import CatalogsList from '@/modules/catalogsList/CatalogsList.vue'
import LoginView from '@/modules/auth/Login.vue'
import Net from '@/services/net'
import SetPassword from '@/modules/auth/SetPassword.vue'
import Merch from '@/modules/merch/Merch.vue'
import Whiteboard from '@/modules/whiteboard/Whiteboard.vue'
import WhiteboardsList from '@/modules/whiteboard/WhiteboardsList.vue'
import Orders from '@/modules/orders/Orders.vue'
import { useNotificationStore } from '@/store/notification'
import Forbidden from '@/modules/admin/errors/Forbidden.vue'
import Articles from '@/modules/admin/catalogData/articles/Articles.vue'
import ArticlesDetails from '@/modules/admin/catalogData/articles/ArticlesDetails.vue'
import { privileges } from '@/models/constants'
import Requests from '@/modules/requests/Requests.vue'
import SeasonlessRequests from '@/modules/admin/main/seasonlessRequests/SeasonlessRequests.vue'
import SeasonlessRequestsDetails from '@/modules/admin/main/seasonlessRequests/SeasonlessRequestsDetails.vue'
import Favorites from '@/modules/favorites/Favorites.vue'

const { t } = i18n.global

const routes = [
  {
    path: '/',
    name: 'Home',
    redirect: { name: 'CatalogsList' },
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: { requiresAuth: false },
  },
  {
    path: '/set-password',
    name: 'SetPassword',
    component: SetPassword,
    meta: { requiresAuth: false },
  },
  {
    path: '/catalogs',
    name: 'CatalogsList',
    component: CatalogsList,
    meta: { requiresAuth: true },
  },
  {
    path: '/catalogs/:catalogCode',
    name: 'OpenCatalog',
    component: CatalogsList,
    meta: { requiresAuth: true },
  },
  {
    path: '/catalogs/:catalogCode/c/:customerId/w/:wholesalePG?/r/:retailPG?/o/:outletPG?',
    component: CatalogBase,
    meta: { requiresAuth: true },
    children: [
      {
        path: 'browse',
        name: 'Catalog',
        component: Browse,
        children: [
          {
            path: 'a/:articleId/:objectId?/:linkedCatalogCode?',
            name: 'CatalogArticleDetails',
            component: Browse,
          },
        ],
      },
      {
        path: 'favorites',
        name: 'Favorites',
        component: Favorites,
        children: [
          {
            path: 'a/:articleId/:objectId?/:linkedCatalogCode?',
            name: 'FavoritesArticleDetails',
            component: Favorites,
          },
        ],
      },
      {
        path: 'merch',
        name: 'Merch',
        component: Merch,
        children: [
          {
            path: 'a/:articleId/:objectId?/:linkedCatalogCode?',
            name: 'MerchArticleDetails',
            component: Merch,
          },
        ],
      },
      {
        path: 'whiteboards',
        name: 'Whiteboards',
        component: WhiteboardsList,
      },
      {
        path: 'whiteboards/:whiteboardId',
        name: 'WhiteboardDetails',
        component: Whiteboard,
        children: [
          {
            path: 'a/:articleId/:objectId?/:linkedCatalogCode?',
            name: 'WhiteboardDetailsArticleDetails',
            component: Whiteboard,
          },
        ],
      },
      {
        path: 'orders',
        name: 'Orders',
        component: Orders,
        meta: { requiresAuth: true },
      },
      {
        path: 'orders/:orderId',
        name: 'Orderlines',
        component: Orders,
        meta: { requiresAuth: true },
      },
      {
        path: 'admin',
        name: 'Admin',
        component: Admin,
        children: [
          {
            path: '403',
            name: 'AdminForbidden',
            component: Forbidden,
          },
          // MAIN MODULES WHICH ARE NOT RELATED TO CATALOG
          {
            path: 'seasonless-requests',
            component: SeasonlessRequests,
            meta: { privileges: privileges.admin.nav.seasonlessRequests },
            children: [
              {
                path: '',
                name: 'SeasonlessRequests',
                component: SeasonlessRequests,
              },
              {
                path: ':requestId',
                name: 'SeasonlessRequestsDetails',
                component: SeasonlessRequestsDetails,
                meta: { privileges: privileges.admin.nav.seasonlessRequestDetails },
              },
            ],
          },
          // CATALOG RELATED MODULES
          {
            path: 'articles',
            name: 'AdminArticles',
            component: Articles,
            meta: { privileges: privileges.admin.nav.articles },
          },
          {
            path: 'articles/:articleId',
            name: 'ArticleDetails',
            component: ArticlesDetails,
            meta: { privileges: privileges.admin.nav.articleDetails },
          },
          {
            path: 'requests',
            name: 'Requests',
            component: Requests,
            meta: { privileges: privileges.admin.nav.requests },
          },
        ],
        redirect: { name: 'AdminArticles' },
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach((to, from, next) => {
  if (to.name !== from.name && to.matched.some(record => record.meta.requiresAuth)) {
    if (!Auth.hasValidToken()) {
      next({
        name: 'Login',
        query: {
          redirect: to.fullPath,
        },
      })
    }
    else {
      next()
    }
  }
  else {
    next()
  }
})

Net.onError((source, err) => {
  const notificationStore = useNotificationStore()

  if (axios.isAxiosError(err) && err.response) {
    if (err.response.status === 401) {
      Auth.setLocalToken('')
      if (router.currentRoute.value.name !== 'Login') {
        router.push({ name: 'Login' })
      }
    }
    else if (err.response.status === 403) {
      const url = (err.config) ? `${(err.config.method || '').toUpperCase()} ${err.config.url}` : ''
      notificationStore.addNotification({ message: t('notifications.privilegeError'), type: 'Alert', url, actions: ['ShowDetails', 'Support'] })
    }
    else {
      let errorMessages = ''
      if (err.response && err.response.data) {
        if (Array.isArray(err.response.data)) {
          errorMessages = err.response.data
            .map(item => item.ErrorMessage)
            .filter(msg => msg)
            .join(',')
        }
        else if (typeof err.response.data === 'object') {
          errorMessages = err.response.data.ErrorMessage || JSON.stringify(err.response.data)
        }
        else if (typeof err.response.data === 'string') {
          errorMessages = err.response.data
        }
      }
      else {
        errorMessages = JSON.stringify(err.toJSON())
      }
      notificationStore.addNotification({ message: t('notifications.networkError'), type: 'Alert', details: errorMessages, actions: ['ShowDetails', 'Support'] })
    }
  }
})

export default router
